import { useEffect, useState } from "react";
import { UserImage } from "@circleco/ui-kit";
import { t } from "@/i18n-js/instance";
import type { AgentProp } from "@/react/components/SettingsApp/CommunityBot/agentsQueries";
import { Icon } from "@/react/components/shared/Icon";
import { reactQueryGet } from "@/react/helpers/backendRequestHelpers";
import { settingsNavPathV3 } from "@/react/helpers/urlHelpers/settingsNavPathV3";
import { internalApi } from "@circle-react/helpers/urlHelpers";
import { Select } from "@circle-react-uikit/Select";
import { Typography } from "@circle-react-uikit/Typography";

export const AiAgentSelect = ({
  selectedId,
  setValue,
  placeholderKey,
  notFoundKey,
  enabledStatus = null,
}: {
  selectedId: number | null;
  setValue: (value: number) => void;
  placeholderKey?: string;
  notFoundKey?: string;
  enabledStatus?: boolean | null;
}) => {
  const [agents, setAgents] = useState<AgentProp[]>([]);

  const agentOptions = agents.map(agent => ({
    text: agent.name,
    value: String(agent.id),
    listItemText: (
      <div className="flex items-start px-2" key={agent.id}>
        {agent.avatar_url ? (
          <UserImage
            src={agent.avatar_url}
            name={agent.name}
            size="5"
            isFullRounded
          />
        ) : (
          <Icon type="20-ai-bot" size={20} />
        )}

        <div className="ml-3 flex min-w-0 flex-col">
          <Typography.LabelSm weight="medium">{agent.name}</Typography.LabelSm>
        </div>
      </div>
    ),
  }));

  const manageAgentsOption = {
    text: t("settings.community_bot.agents.manage_agents"),
    value: "manage_agents",
    listItemText: (
      <div className="flex items-start px-2" key="manage_agents">
        <Icon type="20-plus-v3" size={20} />
        <div className="ml-3 flex min-w-0 flex-col">
          <Typography.LabelSm weight="medium">
            {t("settings.community_bot.agents.manage_agents")}
          </Typography.LabelSm>
        </div>
      </div>
    ),
  };

  const data = [...agentOptions, manageAgentsOption];

  const searchAgents = (query: string) => {
    const params: { name: string; enabled?: boolean } = {
      name: query,
    };

    if (typeof enabledStatus === "boolean") {
      params.enabled = enabledStatus;
    }

    void reactQueryGet(
      internalApi.communityBot.agents.search({
        params,
      }),
    ).then(response => {
      setAgents(response.records);
    });
  };

  useEffect(() => {
    searchAgents("");
  }, []);

  return (
    <Select
      name="community_bot_profile_id"
      placeholder={placeholderKey && t(placeholderKey)}
      onSearch={searchAgents}
      onChange={value => {
        if (value?.value === "manage_agents") {
          window.open(
            settingsNavPathV3.community_bot.agents,
            "_blank",
            "noopener,noreferrer",
          );
        } else {
          setValue(Number(value?.value));
        }
      }}
      data={data}
      direction="down"
      value={String(selectedId)}
      showSearchIcon={false}
      notFoundContent={notFoundKey && t(notFoundKey)}
      hideIcon
    />
  );
};
